import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FloppyDiskIcon } from '@phosphor-icons/react/dist/csr/FloppyDisk';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';

import { mapDetails } from './referenceFormHelpers';
import { Button } from '../../design-system/primitives/Button/Button';
import { Dialog } from '../../design-system/composites/Dialog/Dialog';
import { Input } from '../../design-system/primitives/Input/Input';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
} from '../../lib/api/client';
import { dispensariesApi, type Dispensary, type DispensaryInput } from '../../lib/api/reference';
import styles from './ReferenceFormDialog.module.css';

/**
 * The create/edit form for a dispensary — the root of the hierarchy, so it
 * has no parent to pick. Follows `UserFormDialog.tsx` for the shape: one
 * component, `node` absent is create, present is edit; unlike that dialog,
 * there is no second call and no diffing — every field is `sometimes` on the
 * server (`DispensaryRequestBase`), so an edit simply resends everything the
 * form currently holds, which is a no-op for anything the caller left
 * unchanged.
 */

export interface DispensaryFormDialogProps {
  open: boolean;
  node?: Dispensary;
  onClose: () => void;
  onSaved: (node: Dispensary) => void;
}

interface FormState {
  externalCode: string;
  nameEn: string;
  nameAr: string;
  isActive: boolean;
}

function stateFor(node: Dispensary | undefined): FormState {
  if (node === undefined) {
    return { externalCode: '', nameEn: '', nameAr: '', isActive: true };
  }
  return {
    externalCode: node.external_code,
    nameEn: node.name_en,
    nameAr: node.name_ar,
    isActive: node.is_active,
  };
}

function toInput(state: FormState): DispensaryInput {
  return {
    external_code: state.externalCode.trim(),
    name_en: state.nameEn.trim(),
    name_ar: state.nameAr.trim(),
    is_active: state.isActive,
  };
}

type MissingField = 'external_code' | 'name_en' | 'name_ar';

/** Neither has an exported constant in `client.ts` yet — the same trap
 *  `UserActions.tsx`/`UserFormDialog.tsx` already document. */
const FORBIDDEN_CODE = 'forbidden';

export function DispensaryFormDialog({ open, node, onClose, onSaved }: DispensaryFormDialogProps) {
  const { t } = useTranslation();
  const isEdit = node !== undefined;

  const [state, setState] = useState<FormState>(() => stateFor(node));
  const [missing, setMissing] = useState<readonly MissingField[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [generalFailure, setGeneralFailure] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const inFlight = useRef(false);
  const codeRef = useRef<HTMLInputElement>(null);

  // The dialog instance is reused across opens — create, then an edit of a
  // different row — rather than remounted, so every field resets whenever it
  // opens, keyed on the row it is about to show. Mirrors UserFormDialog.tsx.
  useEffect(() => {
    if (!open) return;
    setState(stateFor(node));
    setMissing([]);
    setFieldErrors({});
    setGeneralFailure(null);
  }, [open, node]);

  function describeFailure(error: ApiError): string {
    switch (error.code) {
      case FORBIDDEN_CODE:
        return t('reference.notPermitted');
      case TOO_MANY_REQUESTS_CODE:
        return t('reference.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('reference.actionFailed');
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (inFlight.current) return;

    const empty: MissingField[] = [];
    if (state.externalCode.trim() === '') empty.push('external_code');
    if (state.nameEn.trim() === '') empty.push('name_en');
    if (state.nameAr.trim() === '') empty.push('name_ar');

    if (empty.length > 0) {
      setMissing(empty);
      return;
    }
    setMissing([]);

    inFlight.current = true;
    setSubmitting(true);
    setFieldErrors({});
    setGeneralFailure(null);

    try {
      const input = toInput(state);
      const saved =
        isEdit && node !== undefined
          ? await dispensariesApi.update(node.uuid, input)
          : await dispensariesApi.create(input);
      onSaved(saved);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      if (Object.keys(error.details).length > 0) {
        setFieldErrors(mapDetails(error.details));
      } else {
        setGeneralFailure(describeFailure(error));
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  const dialogTitle =
    isEdit && node !== undefined
      ? t('reference.editDispensaryTitle', { name: node.name_en })
      : t('reference.createDispensaryTitle');

  return (
    <Dialog open={open} size="lg" onClose={onClose} initialFocusRef={codeRef} title={dialogTitle}>
      <form
        className={styles['form']}
        method="post"
        noValidate
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        {/* Mounted from the first frame, empty — see UserFormDialog.tsx for
            the same shape. */}
        <div
          className={styles['failure']}
          role="alert"
          aria-atomic="true"
          data-testid="dispensaryFormFailure"
        >
          {generalFailure}
        </div>

        {/*
          M-19 Task 5: `width="code"` is Field.module.css's 90px lane —
          "1-character assessment code" per its own handoff comment.
          `external_code` is validated at `max:150` server-side
          (`DispensaryRequestBase::rules()`, matching the `varchar(150)`
          column `2026_08_01_000200_create_reference_tables.php` defines) —
          `todo.txt`'s "dispensaries code field should be full length" is
          this field sitting in a lane built for a value 150x shorter than
          what the API actually accepts. `maxLength` enforces the same
          number client-side rather than leaving it to a 422 round-trip.
        */}
        <Input
          ref={codeRef}
          label={t('reference.fieldCode')}
          width="fill"
          maxLength={150}
          dir="ltr"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          required
          value={state.externalCode}
          error={
            missing.includes('external_code')
              ? t('reference.required')
              : fieldErrors['external_code']
          }
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, externalCode: value }));
          }}
        />

        <Input
          label={t('reference.fieldNameEn')}
          width="fill"
          dir="ltr"
          required
          value={state.nameEn}
          error={missing.includes('name_en') ? t('reference.required') : fieldErrors['name_en']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, nameEn: value }));
          }}
        />

        <Input
          label={t('reference.fieldNameAr')}
          width="fill"
          dir="rtl"
          required
          value={state.nameAr}
          error={missing.includes('name_ar') ? t('reference.required') : fieldErrors['name_ar']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, nameAr: value }));
          }}
        />

        <label className={styles['checkboxRow']}>
          <input
            type="checkbox"
            className={styles['checkbox']}
            checked={state.isActive}
            onChange={(event) => {
              const { checked } = event.target;
              setState((current) => ({ ...current, isActive: checked }));
            }}
          />
          {t('reference.fieldActive')}
        </label>

        <div className={styles['actions']}>
          <Button type="submit" loading={submitting} icon={<FloppyDiskIcon size={16} />}>
            {t('reference.save')}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose} icon={<XIcon size={16} />}>
            {t('reference.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
